import { forwardRef, HTMLAttributes } from 'react';

import { useLiquidityScore } from '../../hooks/useLiquidityScore';
import useTokenResult from '../../hooks/useTokenResult';
import { fontStyles } from '../../styles/typography';
import { ChainAddress } from '../../utils/models';
import { TextSkeleton } from '../Skeleton';
import { getScoreIcon } from '../TokenProfile/TokenProfileLinks/LiquidityScore/getScoreIcon';

type Props = {
  address: ChainAddress;
  chainId: number;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Pre-fetched liquidity score. When provided, skips internal fetch. */
  score?: number;
  /** Whether the score is currently loading. Used when score is provided externally. */
  scoreLoading?: boolean;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'onClick'>;

export const LiquidityScoreBadge = forwardRef<HTMLDivElement, Props>(function LiquidityScoreBadge(
  {
    address,
    chainId,
    className,
    onClick,
    score: scoreProp,
    scoreLoading: scoreLoadingProp,
    ...divProps
  },
  forwardedRef,
) {
  // Only fetch internally if score is not provided by parent
  const hasExternalScore = scoreProp !== undefined || scoreLoadingProp !== undefined;
  const { data: internalData, isLoading: internalIsLoading } = useLiquidityScore(
    hasExternalScore ? undefined : [{ address, chainId }],
  );

  const { token: tokenResult } = useTokenResult(address, chainId);

  const isLoading = hasExternalScore ? (scoreLoadingProp ?? false) : internalIsLoading;
  const rawScore = hasExternalScore ? scoreProp : internalData?.data?.[0]?.score;

  const score = (tokenResult?.tokenListsBlock?.length ?? 0) === 0 ? rawScore : undefined;

  const icon = isLoading ? getScoreIcon(undefined) : getScoreIcon(score);
  const label = isLoading ? (
    <TextSkeleton width={14} height={16} lineClassName="mb-0!" />
  ) : (
    (score ?? '-')
  );

  return (
    <div
      ref={forwardedRef}
      onClick={onClick}
      className={fontStyles({
        variant: 'LABEL_SM',
        class: [
          'items-center',
          'rounded-2xl',
          'flex',
          'justify-center',
          'h-8',
          'px-4',
          'gap-1.5',
          'min-w-[68px]',
          'w-fit',
          'outline-none',
          'bg-token-alphalight-300',
          'hover:bg-token-alphalight-400',
          'active:bg-token-alphalight-400',
          'focus-visible:outline-token-alphalight-500',
          'focus-visible:outline-2',
          'focus-visible:outline-solid',
          className,
        ],
      })}
      {...divProps}
    >
      {icon}
      {label}
    </div>
  );
});
